// frontend/src/lib/api/categoryService.ts
import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

export interface Category {
  category_id: number;
  name: string;
  description?: string;
  icon?: string;
  slug_url?: string;
  is_active?: boolean;
  parent_category_id?: number | null;
  display_order?: number;
  subcategory_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CategoryFilters {
  page?: number;
  limit?: number;
  search?: string;
  parent?: string;
  status?: string;
}

export interface CategoryFormData {
  name: string;
  description?: string;
  icon?: string;
  parent_category_id?: number | null;
  display_order?: number;
  is_active?: boolean;
}

class CategoryService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/categories',
      timeout: 10000,
    });
  }

  // Method names that hooks expect
  async getAll(filters: CategoryFilters = {}): Promise<ApiResponse<{
    categories: Category[];
    pagination: any;
    filters: any;
    stats: any;
  }>> {
    return this.get('', { params: filters });
  }

  async getById(id: number): Promise<ApiResponse<{ category: Category }>> {
    return this.get(`/${id}`);
  }

  async create(data: CategoryFormData): Promise<ApiResponse<{ category: Category }>> {
    return this.post('', data);
  }

  async update(id: number, data: CategoryFormData): Promise<ApiResponse<{ category: Category }>> {
    return this.put(`/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/${id}`);
  }

  async toggleStatus(id: number): Promise<ApiResponse<{ category: Category }>> {
    return this.post(`/${id}/toggle-status`);
  }

  // ✅ ADDED: Standard method names for consistency (what useFilterOptions expects)
  async getCategories(filters: CategoryFilters = {}): Promise<ApiResponse<Category[]>> {
    const response = await this.getAll(filters);
    if (response.success && response.data?.categories) {
      return {
        ...response,
        data: response.data.categories
      };
    }
    return response as ApiResponse<Category[]>;
  }

  async getCategoryById(id: number): Promise<ApiResponse<Category>> {
    const response = await this.getById(id);
    if (response.success && response.data?.category) {
      return {
        ...response,
        data: response.data.category
      };
    }
    return response as ApiResponse<Category>;
  }

  async createCategory(data: CategoryFormData): Promise<ApiResponse<Category>> {
    const response = await this.create(data);
    if (response.success && response.data?.category) {
      return {
        ...response,
        data: response.data.category
      };
    }
    return response as ApiResponse<Category>;
  }

  async updateCategory(id: number, data: CategoryFormData): Promise<ApiResponse<Category>> {
    const response = await this.update(id, data);
    if (response.success && response.data?.category) {
      return {
        ...response,
        data: response.data.category
      };
    }
    return response as ApiResponse<Category>;
  }

  async deleteCategory(id: number): Promise<ApiResponse<void>> {
    return this.delete(id);
  }

  async toggleCategoryStatus(id: number): Promise<ApiResponse<Category>> {
    const response = await this.toggleStatus(id);
    if (response.success && response.data?.category) {
      return {
        ...response,
        data: response.data.category
      };
    }
    return response as ApiResponse<Category>;
  }
}

export const categoryService = new CategoryService();